/**
 *
 * ReportPeriodicityForm
 *
 */
import { Fragment } from 'react';

import { Button } from '@dec/shared';
import { ButtonAction } from '@dec/shared';
import { Combobox, Datepicker, HookForm, Number } from '@dec/shared';
import { Col, Row } from '@dec/shared';
import { BtnGroup } from '@dec/shared';
import { BtnToolbar } from '@dec/shared';
import { factoryYup } from 'app/core/factory-yup';
import { notificationSuccess } from 'app/core/toast';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../../../../../core/dialog';
import { messages } from '../../../../../Operations/EconomyActivity/translation';
import { type CatalogModel } from '../../../../../Parameters/Catalog/Catalog/models/types';
import { label as labelCommon } from '../../../../../translation';
import { usePeriodicityTypes } from '../../../services/hooks';
import {
  createReportPeriodicity,
  deleteReportPeriodicity,
  updateReportPeriodicity
} from '../../../services/http';
import { label } from '../../../translation';
import { type ReportPeriodicity } from '../types';

const yup = factoryYup();
interface Props {
  reportId: string;
  defaultValue: ReportPeriodicity;
}

interface ReportPeriodicityRequest {
  periodicityType: CatalogModel;
  id?: string;
  filingDueDay?: number;
  dateFrom?: Date;
  dateTo?: Date;
}

export function ReportPeriodicityForm(props: Props) {
  const { t } = useTranslation();
  const validations = yup.object().shape({
    periodicityType: yup
      .object()
      .required(i18next.t('reports.validations.periodicityTypeRequired')),
    filingDueDay: yup
      .number()
      .nullable()
      .required(i18next.t('reports.validations.filingDueDayRequired'))
      .max(99),
    dateFrom: yup.date().required(i18next.t('reports.validations.dateFromRequired')),
    dateTo: yup.date().nullable()
  });

  const navigate = useNavigate();

  const { reportId, defaultValue } = props;

  const [{ data: reportPeriodicityTypes, loading, error: errorPerson }] = usePeriodicityTypes();

  const savePeriodicity = async (reportPeriodicity: ReportPeriodicityRequest) => {
    reportPeriodicity.id = defaultValue.id;
    const payload = {
      ...reportPeriodicity,
      periodicityTypeId: reportPeriodicity.periodicityType.id
    };
    try {
      if (payload.id) {
        await updateReportPeriodicity(reportId, payload.id, payload as ReportPeriodicity);
      } else {
        await createReportPeriodicity(reportId, payload as ReportPeriodicity);
      }
      notificationSuccess(i18next.t('reports.messages.savedSuccessfully'));
      navigate(`/taxes/manage/complementary/${reportId}/periodicity`);
    } catch (e) {
      console.error(e);
    }
  };

  const onCancel = () => {
    navigate(`/taxes/manage/complementary/${props.reportId}/periodicity`);
  };

  return (
    <Fragment>
      <HookForm initialValues={defaultValue} validations={validations} onSubmit={savePeriodicity}>
        <Row>
          <Col>
            <Combobox
              display="name"
              code="code"
              data={loading ? [] : reportPeriodicityTypes}
              label={label(t, 'periodicityType')}
              name={'periodicityType'}
              defaultValue={null}
            />
          </Col>

          <Col>
            <Number label={label(t, 'filingDueDay')} name={'filingDueDay'} />
          </Col>
        </Row>
        <Row>
          <Col>
            <Datepicker label={label(t, 'dateFrom')} name={'dateFrom'} />
          </Col>
          <Col>
            <Datepicker label={label(t, 'dateTo')} name={'dateTo'} />
          </Col>
        </Row>
        <BtnToolbar>
          <BtnGroup>
            <Button action={ButtonAction.cancel} variant="secondary" onClick={onCancel} />
            <Button
              visible={defaultValue?.id !== undefined}
              action={ButtonAction.delete}
              variant={'danger'}
              onClick={async () => {
                const ok = await ConfirmDialog({
                  titleModal: messages(t, 'confirmTitle'),
                  message: messages(t, 'confirmDeleted'),
                  okButton: labelCommon('ok'),
                  cancelButton: labelCommon('cancel')
                });
                if (ok) {
                  try {
                    await deleteReportPeriodicity(reportId, defaultValue.id as string);
                    notificationSuccess(i18next.t('reports.messages.deletedSuccessfully'));
                    navigate(`/taxes/manage/complementary/${reportId}/periodicity`);
                  } catch (e) {
                    console.error(e);
                  }
                }
              }}
            />
          </BtnGroup>
          <BtnGroup>
            <Button action={ButtonAction.save} variant="primary" type={'submit'} />
          </BtnGroup>
        </BtnToolbar>
      </HookForm>
    </Fragment>
  );
}
